import React, { useRef, useState } from "react";
import { Upload, X, Link2, Loader2, Images } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";

const kb = (b) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);

/**
 * Image field with direct upload from the user's computer, plus URL paste.
 */
export const ImageUploadField = ({ value, onChange, label = "Image", testId = "image" }) => {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [library, setLibrary] = useState(null);

  const openLibrary = async () => {
    setLibraryOpen(true);
    if (library) return;
    try {
      const { data } = await api.get("/media");
      setLibrary(data);
    } catch (err) {
      toast.error(formatApiError(err));
      setLibrary([]);
    }
  };

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\/(jpeg|png|webp|gif)$/.test(file.type)) {
      toast.error("Please choose a JPG, PNG, WEBP or GIF image.");
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error("That image is larger than 25 MB. Please compress it first.");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setProgress(0);
    try {
      const { data } = await api.post("/media/upload", fd, {
        onUploadProgress: (p) =>
          setProgress(p.total ? Math.round((p.loaded * 100) / p.total) : 50),
      });
      onChange(data.url);
      setLibrary(null);
      const saved = data.original_size && data.size < data.original_size * 0.95;
      toast.success(
        saved
          ? `${file.name} uploaded — shrunk from ${kb(data.original_size)} to ${kb(data.size)}`
          : `${file.name} uploaded`
      );
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setProgress(null);
    }
  };

  return (
    <div>
      <Label className="text-sm text-[#5C524E] mb-2 block">{label}</Label>
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="w-full sm:w-40 shrink-0">
          {value ? (
            <div className="relative group">
              <img
                src={value}
                alt="Selected"
                data-testid={`${testId}-preview`}
                className="w-full sm:w-40 h-28 object-cover rounded-xl border border-[#A39171]/25 bg-white"
              />
              <button
                type="button"
                onClick={() => onChange("")}
                data-testid={`${testId}-clear`}
                aria-label="Remove image"
                className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-[#2D2623] text-white flex items-center justify-center hover:bg-[#E07A5F] transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <div className="w-full sm:w-40 h-28 rounded-xl border border-dashed border-[#A39171]/40 flex items-center justify-center text-[#A39171] text-xs text-center px-3">
              No image selected
            </div>
          )}
        </div>

        <div className="flex-1 space-y-3">
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={pick}
            data-testid={`${testId}-file-input`}
            className="hidden"
          />
          <Button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={progress !== null}
            data-testid={`${testId}-upload-btn`}
            className="rounded-full bg-[#E07A5F] hover:bg-[#2D2623] text-white h-11 px-6"
          >
            {progress !== null ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading {progress}%
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" /> Upload from computer
              </>
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={openLibrary}
            data-testid={`${testId}-library-btn`}
            className="rounded-full border-[#A39171]/40 h-11 px-6 ml-0 sm:ml-3"
          >
            <Images className="w-4 h-4 mr-2" /> Choose from library
          </Button>
          {progress !== null && (
            <div className="h-1.5 w-full bg-[#A39171]/20 rounded-full overflow-hidden" data-testid={`${testId}-progress`}>
              <div className="h-full bg-[#E07A5F] transition-all" style={{ width: `${progress}%` }} />
            </div>
          )}
          <div className="flex items-center gap-2">
            <Link2 className="w-4 h-4 text-[#A39171] shrink-0" />
            <Input
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              data-testid={`${testId}-url`}
              placeholder="…or paste an image URL"
              className="bg-white/70 border-[#A39171]/30 rounded-xl h-11"
            />
          </div>
          <p className="text-xs text-[#A39171]">
            JPG, PNG, WEBP or GIF · up to 25 MB · large photos are shrunk automatically for fast page loads
          </p>
        </div>
      </div>

      <Dialog open={libraryOpen} onOpenChange={setLibraryOpen}>
        <DialogContent className="max-w-3xl bg-[#FDFBF7] border-[#A39171]/25 rounded-3xl p-8 max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl text-[#2D2623]">Image library</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#5C524E]">
            Every picture you have uploaded. Click one to use it again — nothing is uploaded twice.
          </p>
          {library === null ? (
            <p className="text-[#A39171] py-10 text-center">Loading your images…</p>
          ) : library.length === 0 ? (
            <p className="text-[#A39171] py-10 text-center" data-testid="library-empty">
              No images uploaded yet. Use "Upload from computer" and it will appear here next time.
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mt-2" data-testid="library-grid">
              {library.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  data-testid={`library-item-${m.id}`}
                  onClick={() => {
                    onChange(m.url);
                    setLibraryOpen(false);
                    toast.success("Image selected");
                  }}
                  className={`group text-left rounded-2xl overflow-hidden border transition-all hover:-translate-y-0.5 ${
                    value === m.url ? "border-[#E07A5F] ring-2 ring-[#E07A5F]/30" : "border-[#A39171]/25 hover:border-[#E07A5F]"
                  }`}
                >
                  <img src={m.url} alt={m.original_filename || "Uploaded image"} className="w-full h-24 object-cover bg-white" />
                  <span className="block px-3 py-2 text-[11px] text-[#5C524E] truncate">
                    {m.original_filename || "image"}
                  </span>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImageUploadField;
